import Seo from "@/components/Seo";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { api, DONATE_URL } from "@/lib/api";
import { RevealLines, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import SubstackCTA from "@/components/SubstackCTA";
import LatestSubstack from "@/components/LatestSubstack";
import PhotoOfMonth from "@/components/PhotoOfMonth";

const HERO_IMG =
  "https://images.unsplash.com/photo-1539702169544-c0bcff87fcd7?crop=entropy&cs=srgb&fm=jpg&q=85";
const LIFT_IMG =
  "https://images.unsplash.com/photo-1611432579402-7037e3e2c1e4?crop=entropy&cs=srgb&fm=jpg&q=85";

const CHAPTERS = [
  { n: "01", t: "Listen", d: "We begin in community — in living rooms and church basements — before a single word is written." },
  { n: "02", t: "Report", d: "Rigorous, human-centered journalism that connects lived experience to systemic trends." },
  { n: "03", t: "Connect", d: "We link the local to the national, the personal to the structural, the story to the data." },
  { n: "04", t: "Act", d: "Solutions-centered reporting that moves communities and decision-makers toward change." },
];

export default function Home() {
  const [articles, setArticles] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    api.get("/articles").then(({ data }) => setArticles(data)).catch(() => {});
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1, 5);

  return (
    <div data-testid="home-page">
      <Seo />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen pt-[92px] overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-8 items-center min-h-[calc(100vh-92px)]">
          <div className="lg:col-span-7 pt-10 lg:pt-0">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="label text-ink/50 mb-6 flex items-center gap-3"
            >
              <span className="h-2 w-2 rounded-full bg-canary" /> Advocacy Journalism · Est. Colorado
            </motion.p>
            <RevealLines
              as="h1"
              className="font-display font-black text-[15vw] sm:text-[13vw] lg:text-[7.4vw] leading-[0.86] tracking-tight"
              lines={["The canaries", "in the coal", "mine."]}
              delay={0.15}
            />
            <FadeUp delay={0.9} className="mt-8 max-w-xl">
              <p className="text-base sm:text-lg text-ink/70 leading-relaxed">
                We document how economic systems impact Black women — using their experiences as
                the earliest indicator of shifts that reach us all.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-8">
                <a
                  href={DONATE_URL} target="_blank" rel="noopener noreferrer"
                  data-testid="hero-donate-btn"
                  className="inline-flex items-center gap-2 label bg-ink text-white px-8 py-4 rounded-full hover:bg-canary hover:text-ink transition-colors duration-300"
                >
                  Fund the reporting <ArrowUpRight size={16} />
                </a>
                <Link
                  to="/photojournalism" data-testid="hero-gallery-btn"
                  className="inline-flex items-center gap-2 label border border-ink px-8 py-4 rounded-full hover:bg-ink hover:text-white transition-colors duration-300"
                >
                  View the gallery
                </Link>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="hover-img relative aspect-[3/4] w-full bg-paper"
            >
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src={HERO_IMG} alt="A dignified portrait" loading="eager"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute -bottom-4 -left-4 bg-canary text-ink px-5 py-4 max-w-[200px]">
                <p className="label leading-tight">Lift Every Voice · Signature Initiative</p>
              </div>
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 items-center gap-2 text-ink/40"
        >
          <ArrowDown size={16} className="animate-bounce" />
          <span className="label">Scroll</span>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="bg-ink text-white py-5 grain relative overflow-hidden">
        <Marquee speed={45} gradient={false} className="relative z-10">
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="font-display italic text-3xl sm:text-4xl px-8 flex items-center gap-8">
              Because the most impacted are often the most impactful.
              <span className="text-canary not-italic font-sans font-bold text-2xl">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-36">
        <FadeUp>
          <p className="label text-ink/50 mb-4">Our Approach</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[0.95]">
            Four movements of a single practice.
          </h2>
        </FadeUp>
        <div className="mt-16 divide-y divide-ink/15 border-t border-ink/15">
          {CHAPTERS.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.06}>
              <div className="grid md:grid-cols-12 gap-4 md:gap-8 py-10 group">
                <div className="md:col-span-2 font-display text-6xl lg:text-7xl font-black text-ink/15 group-hover:text-canary transition-colors duration-500">
                  {c.n}
                </div>
                <h3 className="md:col-span-3 font-display text-3xl lg:text-4xl font-bold">{c.t}</h3>
                <p className="md:col-span-6 md:col-start-7 text-lg text-ink/70 leading-relaxed self-center">
                  {c.d}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* FEATURED STORIES */}
      <section className="bg-paper py-24 lg:py-32">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <FadeUp className="flex items-end justify-between mb-14">
            <div>
              <p className="label text-ink/50 mb-4">The Reporting</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">Latest stories</h2>
            </div>
            <Link to="/articles" data-testid="view-all-articles" className="hidden sm:inline-flex items-center gap-2 label link-underline pb-1">
              View all <ArrowUpRight size={15} />
            </Link>
          </FadeUp>

          {featured && (
            <FadeUp>
              <Link
                to={`/article/${featured.id}`}
                data-testid="featured-article-card"
                className="grid lg:grid-cols-2 gap-8 lg:gap-14 mb-16 group items-center"
              >
                <div className="hover-img aspect-[16/11] bg-ink/10">
                  <img src={featured.cover_image} alt={featured.title} className="h-full w-full object-cover" />
                </div>
                <div>
                  <p className="label text-ink/60 mb-4">{featured.category} · {featured.read_time} min read</p>
                  <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.02] tracking-tight group-hover:text-ink/70 transition-colors">
                    {featured.title}
                  </h3>
                  <p className="mt-5 text-lg text-ink/70 leading-relaxed max-w-xl">{featured.excerpt}</p>
                  <span className="inline-flex items-center gap-2 mt-7 label link-underline pb-1">
                    Read the story <ArrowUpRight size={15} />
                  </span>
                </div>
              </Link>
            </FadeUp>
          )}

          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 border-t border-ink/15 pt-14">
            {rest.map((a) => (
              <StaggerItem key={a.id}>
                <Link to={`/article/${a.id}`} data-testid={`article-card-${a.id}`} className="group block">
                  <div className="hover-img aspect-[4/3] bg-ink/10 mb-5">
                    <img src={a.cover_image} alt={a.title} className="h-full w-full object-cover" />
                  </div>
                  <p className="label text-ink/50 mb-2">{a.category}</p>
                  <h4 className="font-display text-xl font-bold leading-snug group-hover:text-ink/60 transition-colors">
                    {a.title}
                  </h4>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* LIFT EVERY VOICE */}
      <section className="relative bg-ink text-white grain overflow-hidden">
        <div className="relative z-10 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-36 grid lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-6">
            <p className="label text-canary mb-6">Signature Initiative</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Lift Every Voice
            </h2>
            <p className="mt-7 text-lg text-white/70 leading-relaxed max-w-xl">
              A convening of residents, journalists, nonprofit leaders, and public officials —
              turning community expertise into solutions and action across Colorado.
            </p>
            <Link
              to="/about" data-testid="lift-learn-btn"
              className="inline-flex items-center gap-2 mt-9 label bg-white text-ink px-8 py-4 rounded-full hover:bg-canary transition-colors duration-300"
            >
              How it works <ArrowUpRight size={16} />
            </Link>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5 lg:col-start-8">
            <div className="hover-img aspect-[4/5]">
              <img src={LIFT_IMG} alt="Community leader" className="h-full w-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CANARY PHOTO OF THE MONTH */}
      <PhotoOfMonth />

      {/* LATEST FROM SUBSTACK */}
      <LatestSubstack limit={3} />

      {/* SUBSTACK */}
      <SubstackCTA />

      {/* DONATE BAND */}
      <section className="bg-canary text-ink">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <FadeUp>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl leading-[1.02]">
              Independent journalism is built by the community it serves.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <a
              href={DONATE_URL} target="_blank" rel="noopener noreferrer"
              data-testid="band-donate-btn"
              className="inline-flex items-center gap-2 label bg-ink text-white px-9 py-5 rounded-full hover:bg-white hover:text-ink transition-colors duration-300 whitespace-nowrap"
            >
              Become a supporter <ArrowUpRight size={16} />
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
